/*
 * Copyright (c) 2025 MTa Learning. All Rights Reserved.
 *
 * MTa Learning proprietary information: the enclosed materials contain proprietary information of MTa Learning and
 * shall not be disclosed in whole or in any part to any third party or used by any person for any purpose, without
 * written consent of MTa Learning. Duplication of any portion of these materials shall include this notice.
 */
'use client'

import { Minus, Plus } from 'lucide-react'
import { useTranslations } from 'next-intl'
import React, { useCallback, useState } from 'react'

import { cn } from '@/utils/css'

/** A description of the available properties for this component. */
type NumberInputProps = {
  /** Initial value for the input */
  initialValue?: number

  /** Minimum allowed value (default: 0). */
  min?: number

  /** Maximum allowed value (optional). */
  max?: number

  /** Step value for increment/decrement (default: 1). */
  step?: number

  /** Callback function triggered when the value changes. */
  onChange: (value: number) => void

  /** Optional additional CSS classes for the container. */
  className?: string
}

/**
 * Number input that enables consumers to input numerical values within specified boundaries and with a defined step
 * size. It provides buttons for incrementing and decrementing the value, and allows for custom styling with classNames.
 */
const NumberInput: React.FC<NumberInputProps> = ({
  initialValue = 1,
  min = 0,
  max,
  step = 1,
  onChange,
  className = '',
}) => {
  const t = useTranslations('NumberInput')
  const [currentValue, setCurrentValue] = useState(() => {
    let value = initialValue
    if (min !== undefined && value < min) {
      value = min
    }
    if (max !== undefined && value > max) {
      value = max
    }
    return value
  })

  const handleIncrement = useCallback(() => {
    setCurrentValue((prevValue) => {
      const nextVal = prevValue + step
      if (max !== undefined && nextVal > max) {
        return prevValue
      }

      onChange(nextVal)
      return nextVal
    })
  }, [step, max, onChange])

  const handleDecrement = useCallback(() => {
    setCurrentValue((prevValue) => {
      const nextVal = prevValue - step
      if (min !== undefined && nextVal < min) {
        return prevValue
      }
      onChange(nextVal)
      return nextVal
    })
  }, [step, min, onChange])

  return (
    <div className={cn('inline-flex items-center border border-primary-500 rounded-full p-1.5 space-x-2', className)}>
      <button
        type='button'
        onClick={handleDecrement}
        disabled={min !== undefined && currentValue <= min}
        className={cn(
          'flex items-center justify-center w-6 h-6 bg-primary-500 text-white rounded-full cursor-pointer',
          'focus:outline-hidden focus:ring-2 focus:ring-primary-400 focus:ring-opacity-50',
          'disabled:opacity-50 disabled:cursor-not-allowed',
        )}
        aria-label={t('decrementValueAriaLabel')}
      >
        <Minus className='w-4 h-4' strokeWidth={3} />
      </button>

      <span className='text-lg font-medium text-indigo-900 min-w-16 text-center px-2' aria-live='polite'>
        {currentValue}
      </span>

      <button
        type='button'
        onClick={handleIncrement}
        disabled={max !== undefined && currentValue >= max}
        className={cn(
          'flex items-center justify-center w-6 h-6 bg-primary-500 text-white rounded-full cursor-pointer',
          'focus:outline-hidden focus:ring-2 focus:ring-primary-400 focus:ring-opacity-50',
          'disabled:opacity-50 disabled:cursor-not-allowed',
        )}
        aria-label={t('incrementValueAriaLabel')}
      >
        <Plus className='w-4 h-4' strokeWidth={3} />
      </button>
    </div>
  )
}

export { NumberInput }
